import { useMemo, useState } from 'react';
import {
  Table, TableRow, TableCell, TableHead, TableBody,
  Card, CardContent, Grid, Typography, FormControlLabel, Switch, Chip,
} from '@mui/material';
import { useEffectAsync } from '../reactHelper';
import { prefixString } from '../common/util/stringUtils';
import { formatBoolean } from '../common/util/formatter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import SearchHeader, { filterByKeyword } from './components/SearchHeader';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';

const presetDefinitions = [
  {
    key: 'ignitionOn',
    titleKey: 'eventIgnitionOn',
    subtitle: 'Liga o alerta quando o veículo é ligado.',
    matchType: ['ignitionOn'],
    payload: { type: 'ignitionOn' },
  },
  {
    key: 'ignitionOff',
    titleKey: 'eventIgnitionOff',
    subtitle: 'Liga o alerta quando o veículo é desligado.',
    matchType: ['ignitionOff'],
    payload: { type: 'ignitionOff' },
  },
  {
    key: 'overspeed',
    titleKey: 'eventDeviceOverspeed',
    subtitle: 'Recebe aviso ao ultrapassar o limite de velocidade.',
    matchType: ['deviceOverspeed', 'overspeed'],
    payload: { type: 'deviceOverspeed' },
  },
  {
    key: 'powerCut',
    titleKey: 'eventAlarm',
    subtitle: 'Monitora corte de energia da bateria principal.',
    matchType: ['alarm'],
    matchAlarm: 'powerCut',
    payload: { type: 'alarm', attributes: { alarms: 'powerCut' } },
  },
];

const NotificationsPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [availableNotificators, setAvailableNotificators] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/notifications');
      setItems(await response.json());

      const [typesResponse, notificatorsResponse] = await Promise.all([
        fetchOrThrow('/api/notifications/types'),
        fetchOrThrow('/api/notifications/notificators'),
      ]);

      setTypes(await typesResponse.json());
      setAvailableNotificators((await notificatorsResponse.json()).map((it) => it.type));
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  const defaultNotificators = useMemo(() => {
    const preferred = ['web', 'sms', 'traccar', 'firebase'];
    return preferred.filter((type) => availableNotificators.includes(type)).slice(0, 3).join(',');
  }, [availableNotificators]);

  const availablePresetDefinitions = useMemo(() => presetDefinitions.filter((preset) => (
    preset.matchType.some((type) => types.some((it) => it.type === type))
  )), [types]);

  const findPresetItem = (preset) => items.find((item) => {
    if (!preset.matchType.includes(item.type)) {
      return false;
    }
    if (!preset.matchAlarm) {
      return true;
    }
    return item.attributes?.alarms?.split(/[, ]+/).includes(preset.matchAlarm);
  });

  const handlePresetToggle = async (preset, enabled) => {
    if (enabled) {
      await fetchOrThrow('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...preset.payload,
          always: true,
          notificators: defaultNotificators || 'web,sms',
          description: t(preset.titleKey),
        }),
      });
    } else {
      const existing = findPresetItem(preset);
      if (existing) {
        await fetchOrThrow(`/api/notifications/${existing.id}`, { method: 'DELETE' });
      }
    }
    setTimestamp(Date.now());
  };

  const formatList = (prefix, value) => {
    if (value) {
      return value
        .split(/[, ]+/)
        .filter(Boolean)
        .map((it) => t(prefixString(prefix, it)))
        .join(', ');
    }
    return '';
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedNotifications']}>
      <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {availablePresetDefinitions.map((preset) => {
          const enabled = Boolean(findPresetItem(preset));
          return (
            <Grid item xs={12} md={6} lg={4} key={preset.key}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1">{t(preset.titleKey)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{preset.subtitle}</Typography>
                  <Chip label={t('notificationAlways')} size="small" sx={{ mr: 1, mb: 1 }} />
                  <Chip label={defaultNotificators || 'web,sms'} size="small" sx={{ mb: 1 }} />
                  <FormControlLabel
                    sx={{ display: 'flex', mt: 1 }}
                    control={<Switch checked={enabled} onChange={(e) => handlePresetToggle(preset, e.target.checked)} />}
                    label={enabled ? 'Habilitado' : 'Desabilitado'}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedDescription')}</TableCell>
            <TableCell>{t('notificationType')}</TableCell>
            <TableCell>{t('notificationAlways')}</TableCell>
            <TableCell>{t('sharedAlarms')}</TableCell>
            <TableCell>{t('notificationNotificators')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? items.filter(filterByKeyword(searchKeyword)).map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.description}</TableCell>
              <TableCell>{t(prefixString('event', item.type))}</TableCell>
              <TableCell>{formatBoolean(item.always, t)}</TableCell>
              <TableCell>{formatList('alarm', item.attributes?.alarms)}</TableCell>
              <TableCell>{formatList('notificator', item.notificators)}</TableCell>
              <TableCell className={classes.columnAction} padding="none">
                <CollectionActions itemId={item.id} editPath="/settings/notification" endpoint="notifications" setTimestamp={setTimestamp} />
              </TableCell>
            </TableRow>
          )) : (<TableShimmer columns={5} endAction />)}
        </TableBody>
      </Table>
      <CollectionFab editPath="/settings/notification" ignoreDeviceReadonly />
    </PageLayout>
  );
};

export default NotificationsPage;
